import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Handshake, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Bouton "Activer/Gérer le mode Coop" pour un dossier Courtage parrain.
 * - Visible uniquement pour Admin / VP
 * - Uniquement sur les dossiers parrain (origin === 'parrainage')
 * - Uniquement si module Courtage actif (easy_power_stage)
 * - Uniquement si dossier n'a PAS d'apporteur d'origine
 */
export function CoopModeButton({ prospect, onUpdate }) {
    const [open, setOpen] = useState(false);
    const [apporteurs, setApporteurs] = useState([]);
    const [apporteurId, setApporteurId] = useState(prospect?.coop_apporteur_id || '');
    const [mode, setMode] = useState(prospect?.coop_commission_mode || 'percent');
    const [value, setValue] = useState(prospect?.coop_commission_value ?? '');
    const [saving, setSaving] = useState(false);

    const isActive = !!prospect?.coop_mode_enabled;
    const isCourtage = !!prospect?.easy_power_stage;
    // 🎯 Coop = uniquement pour soulager le VP sur un dossier PARRAINAGE Courtage.
    // Doit matcher exactement la règle backend `_validate_coop_eligibility` :
    // au moins un destinataire dans la cascade, OU parrain_beneficiary_id,
    // OU flag is_parrainage, OU origin === 'parrainage'.
    const _recipients = prospect?.parrainage_snapshot?.recipients || [];
    const isParrainage = (
        prospect?.origin === 'parrainage'
        || prospect?.is_parrainage === true
        || !!prospect?.parrain_beneficiary_id
        || (Array.isArray(_recipients) && _recipients.length > 0)
    );

    useEffect(() => {
        if (!open) return;
        // Charger uniquement les apporteurs des équipes du VP du dossier
        api.get(`/prospects/${prospect.id}/coop-mode/eligible-apporteurs`)
            .then(res => {
                const list = res.data?.apporteurs || [];
                setApporteurs(Array.isArray(list) ? list : []);
            })
            .catch(() => setApporteurs([]));
    }, [open, prospect?.id]);

    useEffect(() => {
        // Sync depuis prospect quand il change
        setApporteurId(prospect?.coop_apporteur_id || '');
        setMode(prospect?.coop_commission_mode || 'percent');
        setValue(prospect?.coop_commission_value ?? '');
    }, [prospect?.coop_apporteur_id, prospect?.coop_commission_mode, prospect?.coop_commission_value]);

    // Si module Courtage inactif → bouton non pertinent
    if (!isCourtage) return null;
    // 🚫 Fix 28/02/2026 — le Mode Coop est réservé aux dossiers Courtage PARRAINAGE
    // (soulager le VP sur la gestion modulaire courtage+parrainage). On n'affiche
    // JAMAIS le bouton sur un dossier VP direct ou VP+apporteur classique.
    // Exception : si Coop est DÉJÀ actif (cas legacy avant ce fix), on garde le
    // bouton visible pour permettre la désactivation propre.
    if (!isParrainage && !isActive) return null;

    // Base de calcul détaillée pour l'affichage clair
    // vpBrute = VP Payeur brute (avant TOUTE déduction)
    const vpBrute = Number(prospect?.ep_commission_vp_payeur_brute
        ?? prospect?.ep_commission_vp
        ?? 0);
    // Total parrainage prélevé (cascade)
    const _snapRecipients = prospect?.parrainage_snapshot?.recipients || [];
    const _baseHt = Number(prospect?.commission_ht_ep || prospect?.commission_totale_ep || 0);
    const _parrainageProjected = _snapRecipients.reduce((sum, r) => {
        const rate = Number(r?.ep_rate || 0);
        return sum + (_baseHt * rate / 100);
    }, 0);
    const parrainsTotal = Number(prospect?.ep_commission_parrains_total || _parrainageProjected || 0);
    // VP disponible pour Coop = VP brut − parrains (déduction TOUJOURS explicite,
    // sans s'appuyer sur ep_commission_vp_payeur_nette qui ne reflète pas la cascade
    // sur les dossiers sans apporteur classique)
    const vpNet = Math.max(0, vpBrute - parrainsTotal);
    const numValue = parseFloat(value) || 0;
    const coopAmount = mode === 'percent'
        ? Math.round(vpNet * (numValue / 100) * 100) / 100
        : numValue;
    const capped = coopAmount > vpNet;
    const finalCoop = capped ? vpNet : coopAmount;
    const finalVp = Math.max(0, vpNet - finalCoop);

    const handleSubmit = async () => {
        if (!apporteurId) { toast.error('Choisis un apporteur'); return; }
        if (!numValue || numValue <= 0) { toast.error('Saisis une valeur'); return; }
        setSaving(true);
        try {
            if (isActive) {
                await api.put(`/prospects/${prospect.id}/coop-mode`, {
                    apporteur_id: apporteurId,
                    mode,
                    value: numValue,
                });
                toast.success('Mode Coop mis à jour');
            } else {
                await api.post(`/prospects/${prospect.id}/coop-mode/activate`, {
                    apporteur_id: apporteurId,
                    mode,
                    value: numValue,
                });
                toast.success('Mode Coop activé');
            }
            setOpen(false);
            onUpdate?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async () => {
        if (!window.confirm(
            "Désactiver le mode Coop ?\n\n" +
            "L'apporteur perdra immédiatement l'accès au dossier.\n" +
            "La commission sera conservée uniquement si le dossier est déjà gagné."
        )) return;
        setSaving(true);
        try {
            const res = await api.post(`/prospects/${prospect.id}/coop-mode/deactivate`);
            toast.success(res.data?.commission_kept
                ? 'Mode Coop désactivé — commission conservée (dossier gagné)'
                : 'Mode Coop désactivé — commission remise à 0');
            setOpen(false);
            onUpdate?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                className={isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                    : "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"}
                data-testid="coop-mode-btn"
                title={isActive ? "Gérer le mode Coop" : "Activer le mode Coop"}
            >
                <Handshake className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{isActive ? "Gérer Coop" : "Activer Coop"}</span>
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="coop-mode-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Handshake className="w-5 h-5 text-amber-600" />
                            {isActive ? "Gérer le mode Coop" : "Activer le mode Coop"}
                        </DialogTitle>
                        <DialogDescription>
                            Un apporteur peut être associé à ce dossier parrain pour l'aider à le mener à bien.
                            Sa commission sera déduite de la part VP.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs">Apporteur Coop</Label>
                            <Select value={apporteurId} onValueChange={setApporteurId}>
                                <SelectTrigger data-testid="coop-apporteur-select">
                                    <SelectValue placeholder={apporteurs.length === 0 ? "Aucun apporteur dans les équipes du VP" : "Choisir un apporteur…"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {apporteurs.length === 0 && (
                                        <div className="px-3 py-2 text-xs text-slate-500">
                                            Aucun apporteur trouvé dans les équipes du VP de ce dossier.
                                        </div>
                                    )}
                                    {apporteurs.map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name} — {u.email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs mb-2 block">Mode de saisie</Label>
                            <RadioGroup value={mode} onValueChange={setMode} className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <RadioGroupItem value="percent" data-testid="coop-mode-percent" />
                                    <span className="text-sm">% de la part VP</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <RadioGroupItem value="amount" data-testid="coop-mode-amount" />
                                    <span className="text-sm">Montant fixe (€)</span>
                                </label>
                            </RadioGroup>
                        </div>

                        <div>
                            <Label className="text-xs">
                                Valeur ({mode === 'percent' ? '%' : '€'})
                            </Label>
                            <Input
                                type="number"
                                min="0"
                                step={mode === 'percent' ? '0.5' : '10'}
                                max={mode === 'percent' ? '100' : undefined}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder={mode === 'percent' ? "Ex : 10" : "Ex : 150"}
                                data-testid="coop-value-input"
                            />
                        </div>

                        {/* Preview répartition */}
                        {numValue > 0 && vpNet > 0 && (
                            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-slate-600">VP Payeur brut :</span>
                                    <span className="font-mono">{vpBrute.toFixed(2)} €</span>
                                </div>
                                {parrainsTotal > 0 && (
                                    <div className="flex justify-between text-slate-600">
                                        <span>− Cascade parrains :</span>
                                        <span className="font-mono">− {parrainsTotal.toFixed(2)} €</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-slate-700 border-t pt-1">
                                    <span>VP disponible :</span>
                                    <span className="font-mono">{vpNet.toFixed(2)} €</span>
                                </div>
                                <div className="flex justify-between text-amber-700">
                                    <span>− Commission Coop :</span>
                                    <span className="font-mono font-semibold">− {finalCoop.toFixed(2)} €</span>
                                </div>
                                <div className="flex justify-between text-emerald-700 border-t pt-1">
                                    <span className="font-semibold">= VP Payeur net final :</span>
                                    <span className="font-mono font-bold">{finalVp.toFixed(2)} €</span>
                                </div>
                                <p className="text-[11px] text-slate-500 pt-1 leading-relaxed">
                                    Formule : {vpBrute.toFixed(2)} € (VP brut){parrainsTotal > 0 ? ` − ${parrainsTotal.toFixed(2)} € (parrains)` : ''} − {finalCoop.toFixed(2)} € (Coop) = <strong>{finalVp.toFixed(2)} €</strong>
                                </p>
                                {capped && (
                                    <p className="text-xs text-red-600 pt-1">
                                        ⚠ Coop plafonnée à la part VP disponible ({vpNet.toFixed(2)} €)
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="flex justify-between gap-2">
                        {isActive && (
                            <Button
                                variant="outline"
                                onClick={handleDeactivate}
                                disabled={saving}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid="coop-deactivate-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Désactiver
                            </Button>
                        )}
                        <div className="flex gap-2 ml-auto">
                            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                                Annuler
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="bg-amber-500 hover:bg-amber-600"
                                data-testid="coop-submit-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                                {isActive ? "Enregistrer" : "Activer"}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
